import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider, Link } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchApplicationFunction, deleteApplicationFunction } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ApplicationFunctionDetailPage() {
  const { businessDomainId, categoryId, functionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/application-categories/${categoryId}/application-functions`;

  const loadData = useCallback(async () => {
    try {
      setData(await fetchApplicationFunction(functionId));
    } catch (e) { notify.apiError(e); }
  }, [functionId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteApplicationFunction(functionId);
      notify.success(t("message.delete.success"));
      navigate(basePath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("applicationCategory.applicationFunction.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/${functionId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("applicationCategory.applicationFunctionName")} value={data.applicationFunctionName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("applicationCategory.applicationTaskName")} value={data.applicationTaskName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={data.applicationFunctionDescription} />
          </Grid>
          <Grid size={12}>
            {data.url ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("common.url")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.url}
                </Button>
              </Box>
            ) : (
              <DetailField label={t("common.url")} value="" />
            )}
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
